import { callFunction } from '@/commands/callFunction'

import {
  SCHEMA_VERSION,
  isLegacyStrategyData,
  isLegacyStateData,
} from '@/services/migration/legacyMapping'

/**
 * Conversion des documents encore au format historique.
 *
 * La conversion elle-même est faite par une Cloud Function, en une passe sur l'ensemble
 * de l'application. Ce module ne fait plus qu'en garantir l'exécution avant les premières
 * lectures : le client n'écrit plus rien.
 *
 * Auparavant, chaque écran convertissait à la volée les documents qu'il consultait. Le
 * résultat était le même, mais la conversion se répétait dans le navigateur de chaque
 * utilisateur et empêchait de fermer les règles d'écriture Firestore.
 *
 * Les fonctions `ensure*` conservent leur signature : les appelants n'ont pas changé. Elles
 * se ramènent toutes à la même promesse, jouée une seule fois par session.
 */

export { SCHEMA_VERSION, isLegacyStrategyData, isLegacyStateData }

let migrationPromise = null

/**
 * Lance la conversion si elle n'a pas déjà été demandée pendant cette session.
 * L'appel serveur est lui-même court-circuité si l'application est déjà convertie.
 */
export function ensureWorldMigrated() {
  if (!migrationPromise) {
    migrationPromise = callFunction('migrateLegacyWorld', {})
      .catch(error => {
        // Une conversion impossible ne doit pas empêcher l'affichage : les documents déjà
        // au format courant restent lisibles. L'échec est signalé pour diagnostic.
        console.error('Conversion des documents historiques impossible', error)
        migrationPromise = null
        return null
      })
  }
  return migrationPromise
}

/** Force une nouvelle passe, après une restauration de sauvegarde par exemple. */
export function forceWorldMigration() {
  migrationPromise = callFunction('migrateLegacyWorld', { force: true })
  return migrationPromise
}

// Les points d'entrée historiques se ramènent tous à la conversion globale : elle est peu
// coûteuse une fois faite, et évite de multiplier les appels document par document.
export const ensureStrategyMigrated = () => ensureWorldMigrated()
export const ensureStateMigrated = () => ensureWorldMigrated()
export const ensureStatesMigrated = () => ensureWorldMigrated()
export const ensureTeamStatesMigrated = () => ensureWorldMigrated()
export const migrateStrategyDataIfNeeded = () => ensureWorldMigrated()
export const migrateStateDataIfNeeded = () => ensureWorldMigrated()

/** Conversion complète, déclenchée depuis l'administration. */
export const migrateEverything = () => forceWorldMigration()

export default {
  SCHEMA_VERSION,
  isLegacyStrategyData,
  isLegacyStateData,
  ensureWorldMigrated,
  forceWorldMigration,
  ensureStrategyMigrated,
  ensureStateMigrated,
  ensureStatesMigrated,
  ensureTeamStatesMigrated,
  migrateStrategyDataIfNeeded,
  migrateStateDataIfNeeded,
  migrateEverything,
}
